import { StringMap } from 'i18next';
import { FREE_DOMAIN_FOR_1_YEAR } from '@wix/premium-marketing-data/dist/src/marketing-data/advanced-plans/features';
import {
  Country,
  PaymentCycle,
  Language,
  Experiments,
  ProductChangeType,
} from '../../enums';
import {
  AdvancedPlansFeature,
  AdvancedPlansProductData,
  VoucherSettings,
} from '../../types/internal-types';
import { filterFeaturesByCountry } from '../renderers/common/filterFeaturesByGeo';
import { filterFeaturesBySpec } from '../renderers/common/filterFeaturesBySpec';
import { filterFeaturesByLanguage } from '../renderers/common/filterFeaturesByLanguage';
import { isEligibleForVoucher } from '../../utils';
import { VoucherType } from '@wix/ambassador-premium-vouchers/types';

type EligbleFeatureFilterItemType = {
  voucherType: VoucherType;
  translationKey: string;
};
// A list of feature that should be removed in case they have been used and the user opened second upgrade.
// In case the user wasn't used the feature - all downgrade plans X this featue => will be marked as '-' instead of 'V'
const EligbleFeatureFilterMapping: EligbleFeatureFilterItemType[] = [
  {
    voucherType: VoucherType.DOMAIN_COUPON,
    translationKey: FREE_DOMAIN_FOR_1_YEAR.translationKey,
  },
];
export class FeaturesBuilder {
  constructor(private features: AdvancedPlansFeature[] = []) {}

  filterOnlyOfferingRelated(productIds: string[]) {
    this.features = this.features.filter((feature) => {
      return productIds.find((id) => feature.includedInProducts.has(id));
    });
    return this;
  }

  filterIncludedInProducts(productIds: string[]) {
    this.features = this.features.map((feature) => {
      const filteredIncludedInProducts = new Map<string, true>();
      const filteredValues = new Map<string, string>();

      productIds.forEach((id) => {
        // Handles boolean features
        if (feature.includedInProducts.has(id)) {
          filteredIncludedInProducts.set(id, true);
        }
        // Handles value features
        if (feature.value) {
          const value = feature.value.get(id);
          if (value) {
            filteredValues.set(id, value);
          }
        }
      });

      return {
        ...feature,
        includedInProducts: filteredIncludedInProducts,
        value: feature.value ? filteredValues : feature.value,
      };
    });
    return this;
  }

  filterByExpriments(experiments: StringMap) {
    this.features = filterFeaturesBySpec(this.features, experiments);
    return this;
  }

  applyGeoFiltering(userCountry: Country) {
    this.features = filterFeaturesByCountry(this.features, userCountry);
    return this;
  }

  applyLangFiltering(userLanguage: Language) {
    this.features = filterFeaturesByLanguage(this.features, userLanguage);
    return this;
  }

  applyGoogleAdsFilter(voucherSettings?: VoucherSettings) {
    if (voucherSettings && !voucherSettings.availableInUserCountry) {
      this.features = this.features.filter((f) => !f.isGoogleVoucher);
    }
    return this;
  }

  filterMonthlyDisabledFeatures(paymentCycle: PaymentCycle) {
    if (paymentCycle === PaymentCycle.MONTHLY) {
      this.features = this.features.filter(
        (feature) => !feature.disabledInMonthlyCycle,
      );
    }
    return this;
  }

  useDynamicAdVoucherMarketingFeature(experiments: StringMap) {
    const keysToReplace = [
      'Advanced.Features_Vouchers_75_USD_Ads',
      'Advanced.Features__Vouchers_2000_RUB_Ads',
      'Advanced.Features_Vouchers_75_GBP_Ads',
    ];
    if (experiments[Experiments.adVouchersPricesFromServer] === 'true') {
      this.features.map((feature) => {
        if (keysToReplace.includes(feature.translationKey)) {
          feature.translationKey = 'features.dynamic_ad_vouchers_raw.text';
          feature.tooltipTranslationKey =
            'features.dynamic_ad_vouchers_raw.tooltip';
        }
      });
    }
    return this;
  }

  onlyMonthlyDisabledFeatures(paymentCycle: PaymentCycle) {
    if (paymentCycle === PaymentCycle.MONTHLY) {
      this.features = this.features.filter(
        (feature) => feature.disabledInMonthlyCycle,
      );
    }
    return this;
  }

  changeOneYearDomainFeature({
    userCountry,
    experiments,
    domainName,
    referralInfo,
  }: {
    userCountry: Country;
    experiments: StringMap;
    domainName?: string;
    referralInfo?: string;
  }) {
    let tooltipTranslationKey, translationKey;
    const ccTldCountries = [Country.FR, Country.BR, Country.DE];
    const isDomainNameInDomainFeatureEnabled =
      experiments[Experiments.DisplayDomainNameInDomainFeature] === 'true';
    const shouldChangeKeys =
      isDomainNameInDomainFeatureEnabled &&
      referralInfo === 'add-domain-purchase-intent' &&
      domainName &&
      isEligibleForVoucher(domainName);

    if (shouldChangeKeys) {
      translationKey =
        'Advanced.Features_Core_Domain_personalization_test_optimization';

      tooltipTranslationKey =
        ccTldCountries.includes(userCountry) || userCountry === Country.BE
          ? 'Advanced.Tooltips_Vouchers_Free_Domain_1_Year_ccTLD_new'
          : 'Advanced.Tooltips_Free_Domain_personalization_test';
    } else {
      translationKey = FREE_DOMAIN_FOR_1_YEAR.translationKey;

      tooltipTranslationKey = ccTldCountries.includes(userCountry)
        ? `${
            FREE_DOMAIN_FOR_1_YEAR.tooltipTranslationKey
          }_${userCountry.toLowerCase()}`
        : FREE_DOMAIN_FOR_1_YEAR.tooltipTranslationKey;
    }

    this.features = this.features.map((feature) =>
      feature.translationKey === FREE_DOMAIN_FOR_1_YEAR.translationKey
        ? {
            ...feature,
            tooltipTranslationKey,
            translationKey,
          }
        : feature,
    );

    return this;
  }

  applySecondUpgradeFilter({
    experiments,
    isUpgrade,
    vouchersTypeEligibility,
    advancedPlansProducts,
    isChangePlan,
  }: {
    experiments: StringMap;
    isUpgrade: boolean;
    vouchersTypeEligibility?: string[] | null;
    advancedPlansProducts: AdvancedPlansProductData[];
    isChangePlan: boolean;
  }) {
    const isActive2ndUpgradeFreeDomainVoucherEnable =
      experiments[Experiments.Active2ndUpgradeFreeDomainVoucher] === 'true';
    const isActive2ndUpgradeFreeDomainVoucherEnablePhase2 =
      experiments[Experiments.Active2ndUpgradeFreeDomainVoucherPhase2] ===
      'true';
    if (
      (isActive2ndUpgradeFreeDomainVoucherEnable ||
        isActive2ndUpgradeFreeDomainVoucherEnablePhase2) &&
      isUpgrade
    ) {
      EligbleFeatureFilterMapping.forEach((eligbleFeatureItem) => {
        const isFeatureEligble =
          !vouchersTypeEligibility ||
          vouchersTypeEligibility.includes(eligbleFeatureItem.voucherType);
        if (isFeatureEligble) {
          // user did not use the voucher - uncheck feature for downgraded plans
          if (isActive2ndUpgradeFreeDomainVoucherEnablePhase2) {
            this.features = this.features.map((feature) =>
              feature.translationKey === eligbleFeatureItem.translationKey
                ? {
                    ...feature,
                    includedInProducts: this.filterDowngradeProducts(
                      feature.includedInProducts,
                      advancedPlansProducts,
                      isChangePlan,
                    ),
                  }
                : feature,
            );
          }
        } else {
          // user used the voucher - the feature should be removed
          this.features = this.features.filter(
            (feature) =>
              feature.translationKey !== eligbleFeatureItem.translationKey,
          );
        }
      });
    }
    return this;
  }

  filterDowngradeProducts(
    includedInProducts: Map<string, true>,
    advancedPlansProducts: AdvancedPlansProductData[],
    isChangePlan: boolean,
  ): Map<string, true> {
    const downgradeProducts = advancedPlansProducts
      .filter((product) =>
        isChangePlan
          ? product.productChangeType !== ProductChangeType.Upgrade
          : !product.hasPurchasableCycles,
      )
      .map((product) => product.id.toString());
    includedInProducts.forEach((_value, key, map) => {
      if (downgradeProducts.includes(key)) {
        map.delete(key);
      }
    });
    return includedInProducts;
  }

  build() {
    return this.features;
  }
}
